// DrawioNode: <figure data-type="image" data-drawio-source="/attachments/{ulid}/{name}.drawio">
//               <img src="/attachments/{ulid}/{name}.png"></figure>
//
// drawio 図を表す DecoratorNode。プレビュー PNG を表示し、「編集」ボタンから
// drawio embed エディタ (drawio_editor.js) を開く。編集ソース (.drawio XML) への
// 紐付け data-drawio-source を import/export で保持する点が ImageNode と異なる
// (ImageNode は figure[data-type=image] を拾うが source を捨てるため、priority で上書き)。

import { DecoratorNode } from "lexical"

export class DrawioNode extends DecoratorNode {
  static getType() { return "drawio" }

  static clone(node) {
    return new DrawioNode(node.__src, node.__sourceUrl, node.__alt, node.__width, node.__key)
  }

  constructor(src, sourceUrl, alt = "", width = null, key) {
    super(key)
    this.__src       = src || ""
    this.__sourceUrl = sourceUrl || ""
    this.__alt       = alt || ""
    this.__width     = width
  }

  getSrc()       { return this.getLatest().__src }
  getSourceUrl() { return this.getLatest().__sourceUrl }
  getAlt()       { return this.getLatest().__alt }
  getWidth()     { return this.getLatest().__width }

  // 編集保存後にプレビュー / ソースを差し替える
  setDiagram(src, sourceUrl) {
    const w = this.getWritable()
    w.__src = src
    w.__sourceUrl = sourceUrl
  }

  setWidth(w) {
    const n = (typeof w === "number") ? Math.round(w) : null
    this.getWritable().__width = (n && n > 0) ? n : null
  }

  isInline()             { return false }
  isKeyboardSelectable() { return true }

  createDOM(_config) {
    const figure = document.createElement("figure")
    figure.setAttribute("data-type", "image")
    figure.setAttribute("data-drawio-source", this.__sourceUrl)
    figure.setAttribute("data-lexical-drawio-node", "")
    figure.contentEditable = "false"
    figure.classList.add("akebono-drawio")

    if (this.__src) {
      const img = document.createElement("img")
      img.src = this.__src
      img.alt = this.__alt
      if (this.__width) img.width = this.__width
      img.draggable = false
      img.loading = "lazy"
      figure.appendChild(img)
    } else {
      const ph = document.createElement("div")
      ph.className = "akebono-drawio-placeholder"
      ph.textContent = "📊 DrawIO 図"
      figure.appendChild(ph)
    }

    // 編集ボタン (drawio_edit.js が surface で委譲処理する)
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "akebono-drawio-edit-btn"
    btn.setAttribute("data-drawio-edit", "")
    btn.textContent = "✏️ 図を編集"
    figure.appendChild(btn)

    return figure
  }

  updateDOM(prevNode, figure) {
    if (prevNode.__sourceUrl !== this.__sourceUrl) {
      figure.setAttribute("data-drawio-source", this.__sourceUrl)
    }
    if (prevNode.__src !== this.__src) {
      const img = figure.querySelector("img")
      if (img) {
        img.src = this.__src
      } else {
        // プレースホルダ→画像へ昇格 (再生成)
        return true
      }
    }
    if (prevNode.__width !== this.__width) {
      const img = figure.querySelector("img")
      if (img) {
        if (this.__width) img.width = this.__width
        else img.removeAttribute("width")
      }
    }
    return false
  }

  decorate(_editor, _config) { return null }

  // figure[data-type=image] かつ data-drawio-source を持つものだけ DrawioNode に。
  // ImageNode (priority 2) より高い priority で claim する。
  static importDOM() {
    return {
      figure: (node) => {
        if (node.getAttribute("data-type") !== "image") return null
        if (!node.getAttribute("data-drawio-source")) return null
        return {
          conversion: (el) => {
            const img = el.querySelector("img")
            return {
              node: $createDrawioNode(
                img?.getAttribute("src") || "",
                el.getAttribute("data-drawio-source") || "",
                img?.getAttribute("alt") || "",
                parseWidth(img?.getAttribute("width"))
              )
            }
          },
          priority: 3,
        }
      },
    }
  }

  exportDOM() {
    const figure = document.createElement("figure")
    figure.setAttribute("data-type", "image")
    figure.setAttribute("data-drawio-source", this.__sourceUrl)

    if (this.__src) {
      const img = document.createElement("img")
      img.setAttribute("src", this.__src)
      if (this.__alt)   img.setAttribute("alt", this.__alt)
      if (this.__width) img.setAttribute("width", String(this.__width))
      figure.appendChild(img)
    }
    return { element: figure }
  }

  static importJSON(s) {
    return $createDrawioNode(s.src, s.sourceUrl, s.alt, s.width)
  }
  exportJSON() {
    return {
      type: "drawio",
      src: this.__src,
      sourceUrl: this.__sourceUrl,
      alt: this.__alt,
      width: this.__width,
      version: 1,
    }
  }
}

function parseWidth(raw) {
  if (!raw) return null
  const n = parseInt(raw, 10)
  return Number.isFinite(n) && n > 0 ? n : null
}

export function $createDrawioNode(src, sourceUrl, alt = "", width = null) {
  return new DrawioNode(src, sourceUrl, alt, width)
}

export function $isDrawioNode(node) {
  return node instanceof DrawioNode
}
